import isEmpty from 'lodash/fp/isEmpty'
import isNumber from 'lodash/fp/isNumber'

import type { Progress } from '../api/endpoints'
import type { HTTPError, MediaTypes, Upload } from '../types'

import {
  getFullMediaUrl,
  maxHeightToViewport,
  maxWidthToContainer,
  slugify,
} from '.'
import endpointsDefinition from '../api/endpoints'
import { toast } from '../components/Toaster'
import { MAX_DIRECT_UPLOAD_SIZE } from '../constants'
import { AllowedMediaSources } from '../enums'
import { Logger } from './log'

export function constraintSize(
  width: number | string | undefined,
  height: number | string | undefined,
  maxWidth?: number | string,
  {
    allowDeepRecompute = true,
    constrainHeightToViewport = true,
    preserveRatio = true,
  }: {
    allowDeepRecompute?: boolean
    constrainHeightToViewport?: boolean | number
    preserveRatio?: boolean
  } = {},
): { height: number | string | undefined; width: number | string | undefined } {
  const maxWidthContainer =
    !isNumber(maxWidth) && isEmpty(maxWidth)
      ? document.querySelector('.bn-container')
      : undefined

  const ratio = isNumber(width) && isNumber(height) ? width / height : 1

  const constrainedWidth =
    isNumber(width) && isNumber(maxWidth) ? Math.min(width, maxWidth) : width
  let computedWidth = isNumber(constrainedWidth)
    ? maxWidthContainer
      ? maxWidthToContainer(maxWidthContainer, constrainedWidth, 1)
      : isNumber(maxWidth)
        ? Math.min(constrainedWidth, maxWidth)
        : constrainedWidth
    : constrainedWidth

  const constrainedHeight =
    !isNumber(computedWidth) || computedWidth === width || !preserveRatio
      ? height
      : computedWidth / ratio
  let computedHeight =
    isNumber(constrainedHeight) &&
    constrainHeightToViewport !== false &&
    (constrainHeightToViewport === true || constrainHeightToViewport > 0)
      ? maxHeightToViewport(
          constrainedHeight,
          constrainHeightToViewport === true
            ? undefined
            : constrainHeightToViewport,
        )
      : constrainedHeight

  if (
    computedWidth === width &&
    computedHeight !== height &&
    isNumber(computedWidth) &&
    allowDeepRecompute
  ) {
    const constrainedSize = constraintSize(
      preserveRatio ? computedHeight : computedWidth * ratio,
      computedHeight,
      maxWidth,
      {
        allowDeepRecompute: false,
        constrainHeightToViewport,
        preserveRatio,
      },
    )

    computedWidth = constrainedSize.width
    computedHeight = constrainedSize.height
  }

  return (!isNumber(width) &&
    isEmpty(width) &&
    !isNumber(height) &&
    isEmpty(height)) ||
    (!maxWidthContainer && !isNumber(maxWidth))
    ? { height, width }
    : {
        height: computedHeight,
        width: computedWidth,
      }
}

export type FileSource = 'drop' | AllowedMediaSources

export type ErrorCallbackReasons =
  | string
  | 'empty_file'
  | 'load_abort'
  | 'load_error'
  | 'read_abort'
  | 'read_error'
  | 'too_big'
  | 'unknown_error'
  | 'unknown_type'
  | 'upload_error'
export type Callbacks = {
  onError?: (
    reason: ErrorCallbackReasons,
    parameters: { error: string | Error; file: File; type: string } & Record<
      string,
      unknown
    >,
  ) => Promise<void> | void
  onProcessingEnd?: (
    item: { file: File; type: string },
    ...rest: Array<unknown>
  ) => Promise<void> | void
  onProcessingStart?: (
    item: { file: File; type: string },
    ...rest: Array<unknown>
  ) => Promise<void> | void
  onProcessingSuccess?: (
    result: {
      file: File
      type: string
      upload: Upload
      url: string
      image?: HTMLImageElement
    },
    ...rest: Array<unknown>
  ) => Promise<void> | void
  onProgress?: (progress: Progress) => void
}

export type HandleFileCallback = (
  file: File,
  source: FileSource,
  callbacks: Callbacks,
  params?: Record<string, unknown>,
) => Promise<void>

export function handleFile(
  logger: Logger,
  endpoints: ReturnType<typeof endpointsDefinition>,
): HandleFileCallback {
  return async (file: File, source: FileSource, callbacks: Callbacks = {}) => {
    const onError =
      callbacks?.onError ??
      ((
        _reason: Parameters<Required<Callbacks>['onError']>['0'],
        parameters: { error: string | Error },
      ) => {
        toast.error(
          parameters?.error?.toString() ||
            'An error occurred when uploading the file',
          { duration: Infinity },
        )
      })
    const onProcessingEnd = callbacks?.onProcessingEnd
    const onProcessingStart = callbacks?.onProcessingStart
    const onProcessingSuccess = callbacks?.onProcessingSuccess
    const onProgress = callbacks?.onProgress

    if (!file || file.size === 0) {
      if (logger) {
        logger.error(`The given file from ${source} is empty`, {
          file,
          source,
        })
      }

      onError?.('empty_file', {
        error: `The given file from ${source} is empty`,
        file,
        type: file?.type ?? 'unknown',
      })
      onProcessingEnd?.({ file, type: file?.type ?? 'unknown' })

      return Promise.reject()
    }

    let type = file.type.split('/')[0] || file.type
    if (type !== 'image' && type !== 'audio' && type !== 'video') {
      type = 'file'
    }

    if (file.size > MAX_DIRECT_UPLOAD_SIZE) {
      if (logger) {
        logger.error(`The given file from ${source} is too big`, {
          file,
          max: MAX_DIRECT_UPLOAD_SIZE,
          size: file.size,
          source,
        })
      }

      onError?.('too_big', {
        error: `The given file from ${source} is too big`,
        file,
        max: MAX_DIRECT_UPLOAD_SIZE,
        type,
      })
      onProcessingEnd?.({ file, type })

      return Promise.reject()
    }

    onProcessingStart?.({ file, type })

    if (type === 'image') {
      return new Promise<void>((resolve, reject) => {
        const imageFileReader = new FileReader()

        imageFileReader.onload = () => {
          const image = new Image()

          image.onload = async () => {
            try {
              const [upload] = await endpoints.upload.media(
                file,
                slugify(`${source}-${new Date().toISOString()}_${file.name}`),
                undefined,
                onProgress,
              )
              if (isEmpty(upload)) {
                throw new Error('No upload returned')
              }

              const url = getFullMediaUrl(upload?.path)
              if (isEmpty(url)) {
                if (logger) {
                  logger.error(`Unable to upload the image from ${source}`, {
                    file,
                    source,
                  })
                }

                onError?.('upload_error', {
                  error: 'Unexpected error',
                  file,
                  type,
                })

                reject()
              } else {
                onProcessingSuccess?.({
                  file,
                  image,
                  type,
                  upload,
                  url,
                })

                resolve()
              }
            } catch (uploadError) {
              const actualError = uploadError as HTTPError

              if (logger) {
                logger.error(
                  `Unable to upload the image from ${source}: ${actualError.message}`,
                  { file, source },
                  actualError,
                )
              }

              onError?.('upload_error', { error: actualError, file, type })

              reject()
            } finally {
              onProcessingEnd?.({ file, type })
            }
          }

          image.onerror = () => {
            if (logger) {
              logger.error(`Unable to load the image`, {
                file,
                source,
              })
            }

            onError?.('load_error', {
              error: 'Unable to load the image',
              file,
              type,
            })
            onProcessingEnd?.({ file, type })
            reject()
          }

          image.onabort = () => {
            if (logger) {
              logger.warn(
                `Unable to load the image from ${source}, loading has been aborted`,
                { file, source },
              )
            }

            onError?.('load_abort', {
              error: 'Loading of the image as been aborted',
              file,
              type,
            })
            onProcessingEnd?.({ file, type })
            reject()
          }

          image.src = imageFileReader.result as string
        }

        imageFileReader.onerror = () => {
          if (logger) {
            logger.error(`Unable to read the image from ${source}`, {
              file,
              source,
            })
          }

          onError?.('read_error', {
            error: 'Unable to read the image',
            file,
            type,
          })
          onProcessingEnd?.({ file, type })
          reject()
        }

        imageFileReader.onabort = () => {
          if (logger) {
            logger.warn(
              `Unable to read the image from ${source}, loading has been aborted`,
              { file },
            )
          }

          onError?.('read_abort', {
            error: 'Reading of the image as been aborted',
            file,
            type,
          })
          onProcessingEnd?.({ file, type })
          reject()
        }

        imageFileReader.readAsDataURL(file)
      })
    }

    if (type === 'audio') {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<void>(async (resolve, reject) => {
        try {
          const [upload] = await endpoints.upload.media(
            file,
            slugify(`${source}-${new Date().toISOString()}_${file.name}`),
            undefined,
            onProgress,
          )
          if (isEmpty(upload)) {
            throw new Error('No upload returned')
          }

          const url = getFullMediaUrl(upload?.path)
          if (isEmpty(url)) {
            if (logger) {
              logger.error(`Unable to upload the audio from ${source}`, {
                file,
                source,
              })
            }

            onError?.('upload_error', { error: 'Unexpected error', file, type })

            reject()
          } else {
            onProcessingSuccess?.({
              file,
              type,
              upload,
              url,
            })

            resolve()
          }
        } catch (uploadError) {
          const actualError = uploadError as HTTPError

          if (logger) {
            logger.error(
              `Unable to upload the audio from ${source}: ${actualError.message}`,
              { file, source },
              actualError,
            )
          }

          onError?.('upload_error', { error: actualError, file, type })

          reject()
        } finally {
          onProcessingEnd?.({ file, type })
        }
      })
    }

    if (type === 'video') {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<void>(async (resolve, reject) => {
        try {
          const [upload] = await endpoints.upload.media(
            file,
            slugify(`${source}-${new Date().toISOString()}_${file.name}`),
            undefined,
            onProgress,
          )
          if (isEmpty(upload)) {
            throw new Error('No upload returned')
          }

          const url = getFullMediaUrl(upload?.path)
          if (isEmpty(url)) {
            if (logger) {
              logger.error(`Unable to upload the video from ${source}`, {
                file,
                source,
              })
            }

            onError?.('upload_error', { error: 'Unexpected error', file, type })

            reject()
          } else {
            onProcessingSuccess?.({
              file,
              type,
              upload,
              url,
            })

            resolve()
          }
        } catch (uploadError) {
          const actualError = uploadError as HTTPError

          if (logger) {
            logger.error(
              `Unable to upload the video from ${source}: ${actualError.message}`,
              { file, source },
              actualError,
            )
          }

          onError?.('upload_error', { error: actualError, file, type })

          reject()
        } finally {
          onProcessingEnd?.({ file, type })
        }
      })
    }

    if (type === 'file') {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<void>(async (resolve, reject) => {
        try {
          const [upload] = await endpoints.upload.media(
            file,
            slugify(`${source}-${new Date().toISOString()}_${file.name}`),
            undefined,
            onProgress,
          )
          if (isEmpty(upload)) {
            throw new Error('No upload returned')
          }

          const url = getFullMediaUrl(upload?.path)
          if (isEmpty(url)) {
            if (logger) {
              logger.error(`Unable to upload the file from ${source}`, {
                file,
                source,
              })
            }

            onError?.('upload_error', { error: 'Unexpected error', file, type })

            reject()
          } else {
            onProcessingSuccess?.({
              file,
              type,
              upload,
              url,
            })

            resolve()
          }
        } catch (uploadError) {
          const actualError = uploadError as HTTPError

          if (logger) {
            logger.error(
              `Unable to upload the video from ${source}: ${actualError.message}`,
              { file, source },
              actualError,
            )
          }

          onError?.('upload_error', { error: actualError, file, type })

          reject()
        } finally {
          onProcessingEnd?.({ file, type })
        }
      })
    }

    onError?.('unknown_type', { error: 'Unknown file type', file, type })

    return Promise.reject()
  }
}

export async function handleFiles(
  callback: HandleFileCallback,
  files: Array<File> | FileList,
  source: FileSource,
  callbacks: {
    onAllProcessed?: () => void
    onFileProgress?: ({
      current,
      percentage,
      processed,
      total,
    }: {
      percentage: number
      processed: number
      total: number
      current?: File
      done?: File
    }) => void
  } & Callbacks = {},
  parallel = false,
) {
  if (parallel) {
    await Promise.allSettled(
      [...files].map(async (file, index) =>
        callback(file, source, callbacks, {
          forceReplace: index === 0,
        }),
      ),
    )
  } else {
    let index = 0
    let isFirst = true
    for (const file of files) {
      try {
        callbacks.onFileProgress?.({
          current: file,
          percentage: (index / files.length) * 100,
          processed: index,
          total: files.length,
        })

        // eslint-disable-next-line no-await-in-loop
        await callback(file, source, callbacks, {
          forceReplace: isFirst,
        })

        isFirst = false
        index += 1

        callbacks.onFileProgress?.({
          done: file,
          percentage: (index / files.length) * 100,
          processed: index,
          total: files.length,
        })
      } catch {
        // Nothing to do here.
      }
    }
  }

  callbacks.onAllProcessed?.()
}

export function validateSource(
  provider: AllowedMediaSources,
  type: MediaTypes,
) {
  switch (provider) {
    case AllowedMediaSources.Giphy:
    case AllowedMediaSources.Unsplash: {
      return false
    }

    case AllowedMediaSources.Link:
    case AllowedMediaSources.Paste:
    case AllowedMediaSources.Upload: {
      return true
    }

    case AllowedMediaSources.LoremPicsum: {
      return type === 'image'
    }

    default: {
      return false
    }
  }
}
